import React from 'react';
import { FileText, CheckCircle, XCircle, Database, Download, Loader2 } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAPIClient } from '@/hooks/useAPIClient';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';

interface OverviewStatsProps {
  className?: string;
}

export const OverviewStats: React.FC<OverviewStatsProps> = ({ className }) => {
  const apiClient = useAPIClient();
  const { toast } = useToast();

  // Get file data for actual examples (same as ExampleList)
  const { data: fileData } = useQuery({
    queryKey: ['few-shot', 'examples'],
    queryFn: () => apiClient.fewShot.listExamplesFromFile(),
  });

  // Get actual VDB count from stats endpoint
  const { data: vdbStats } = useQuery({
    queryKey: ['few-shot', 'stats'],
    queryFn: () => apiClient.fewShot.getStatistics(),
    staleTime: 60 * 1000,
  });

  // Direct JSON file download mutation (fast, simple)
  const downloadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get('/api/few-shot/from-file');
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.download = `few_shot_examples_${timestamp}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Download erfolgreich',
        description: `${data.examples?.length || 0} Beispiele heruntergeladen`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Download fehlgeschlagen',
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: 'destructive',
      });
    },
  });

  // File stats + actual VDB count
  const fewShotData = React.useMemo(() => {
    if (!fileData?.examples) return null;

    const examples = fileData.examples;
    const goodExamples = examples.filter(ex => ex.is_good_example).length;
    const badExamples = examples.filter(ex => !ex.is_good_example).length;

    return {
      total_examples: examples.length,
      good_examples: goodExamples,
      bad_examples: badExamples,
      vdb_total_count: vdbStats?.vdb_total_count || 0
    };
  }, [fileData, vdbStats]);

  return (
    <div className={`bg-muted/30 rounded-lg px-4 py-3 ${className || ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <span className="text-sm font-medium text-muted-foreground">Übersicht:</span>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium">{fewShotData?.total_examples || 0}</span>
            <span className="text-xs text-muted-foreground">total examples</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm font-medium text-green-600">{fewShotData?.good_examples || 0}</span>
            <span className="text-xs text-muted-foreground">gut</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm font-medium text-red-600">{fewShotData?.bad_examples || 0}</span>
            <span className="text-xs text-muted-foreground">schlecht</span>
          </div>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium">{fewShotData?.vdb_total_count || 0}</span>
            <span className="text-xs text-muted-foreground">VDB examples</span>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadMutation.mutate()}
          disabled={downloadMutation.isPending || !fewShotData?.total_examples}
          title="Alle Beispiele als JSON herunterladen"
        >
          {downloadMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Exportiere JSON</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default OverviewStats;